import React, { useMemo } from "react";
import { array, oneOf } from "prop-types";
import getConfig from "next/config";

import { useTranslation } from "@lib/i18n";
import Typography from "@components/shared/Typography";
import { LinkButton } from "@components/shared/Button";
import WarningIcon from "@components/shared/Icons/Warning";

import {
  JOURNEY_TYPE,
  REGISTRATION_AVAILABILITY_STATUS,
  REGISTRATION_SWITCH_VARIANT,
} from "@shared/constants";
import { useDeeplinkPathname } from "@hooks/useDeeplinkPathname";

const { publicRuntimeConfig } = getConfig();
const { ROOT_URL_PREFIX } = publicRuntimeConfig;

const RegistrationUnavailable = ({
  status,
  otherAvailableJourneys,
  switchVariant,
}) => {
  const { t } = useTranslation();

  const targetJourney = useMemo(() => {
    if (switchVariant === REGISTRATION_SWITCH_VARIANT.TO_CASH) {
      return JOURNEY_TYPE.CASH;
    }
    return otherAvailableJourneys.find(j => j !== JOURNEY_TYPE.CASH) || null;
  }, [switchVariant, otherAvailableJourneys]);

  const deeplinkPathname = useDeeplinkPathname({
    journeyOverride: targetJourney,
  });

  const homeHref = ROOT_URL_PREFIX || "/";
  const href = targetJourney ? deeplinkPathname : homeHref;

  const keyBase = useMemo(() => {
    if (status === REGISTRATION_AVAILABILITY_STATUS.SWITCH_REQUIRED) {
      if (switchVariant === REGISTRATION_SWITCH_VARIANT.TO_CASH) {
        return "registration.unavailable.switch.toCash";
      }
      return "registration.unavailable.switch.toFinance";
    }
    return "registration.unavailable.none";
  }, [status, switchVariant]);

  return (
    <div
      data-id="registration-unavailable"
      className="flex flex-col items-center gap-4 px-4 py-8 my-4 border border-greyLight rounded-md bg-primaryLight"
    >
      <WarningIcon className="text-primaryDark size-8" />
      <Typography className="text-lg font-semibold text-center text-primaryDark">
        {t(`${keyBase}.title`)}
      </Typography>
      <Typography className="text-center text-balance">
        {t(`${keyBase}.description`)}
      </Typography>
      <LinkButton href={href}>{t(`${keyBase}.cta`)}</LinkButton>
    </div>
  );
};

RegistrationUnavailable.propTypes = {
  status: oneOf(Object.values(REGISTRATION_AVAILABILITY_STATUS)).isRequired,
  otherAvailableJourneys: array,
  switchVariant: oneOf([...Object.values(REGISTRATION_SWITCH_VARIANT), null]),
};

RegistrationUnavailable.defaultProps = {
  otherAvailableJourneys: [],
  switchVariant: null,
};

export default RegistrationUnavailable;
